import type { DocVersion } from '../api';
import { Button } from './Button';

/**
 * Histórico de versões (git) de um arquivo + ações de visualizar e reverter.
 *
 * Componente PURO e reusável: não conhece "docs" nem "skills" — recebe a lista de
 * versões e os callbacks. Pode ser usado pela base de docs e pela biblioteca de
 * skills (ambas versionadas por git no backend). A permissão de reverter é decidida
 * pelo caller via `canRevert` (o backend é a verdade; isto só esconde o botão).
 */
export interface VersionHistoryProps {
  versions: DocVersion[];
  /** sha da versão sendo pré-visualizada agora (destaca o item na lista). */
  activeSha?: string | null;
  /** Desabilita as ações enquanto uma operação está em curso. */
  busy?: boolean;
  /** Mostra o botão "reverter" (gate de permissão fica no caller). */
  canRevert?: boolean;
  /** Pré-visualizar o conteúdo desta versão (sem alterar nada). */
  onPreview: (sha: string) => void;
  /** Reverter o arquivo para esta versão (vira um novo commit). */
  onRevert?: (version: DocVersion) => void;
  /** Fecha o painel de histórico (opcional). */
  onClose?: () => void;
}

export function VersionHistory({
  versions,
  activeSha,
  busy = false,
  canRevert = false,
  onPreview,
  onRevert,
  onClose,
}: VersionHistoryProps) {
  return (
    <aside className="ver-history" aria-label="Histórico de versões">
      <div className="ver-history-head">
        <strong>Histórico</strong>
        {onClose && (
          <Button size="sm" variant="ghost" onClick={onClose}>
            fechar
          </Button>
        )}
      </div>

      {versions.length === 0 ? (
        <p className="ver-history-empty">Sem versões ainda.</p>
      ) : (
        <ul className="ver-history-list">
          {versions.map((v) => (
            <li key={v.sha} className={`ver-item ${activeSha === v.sha ? 'is-active' : ''}`}>
              <button
                type="button"
                className="ver-item-main"
                onClick={() => onPreview(v.sha)}
                disabled={busy}
                title="Ver esta versão"
              >
                <span className="ver-item-msg">{v.message || '(sem mensagem)'}</span>
                <span className="ver-item-meta">
                  {v.author} · {formatDate(v.date)} · {v.sha.slice(0, 7)}
                </span>
              </button>
              {canRevert && onRevert && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => onRevert(v)}
                  title="Reverter para esta versão"
                >
                  reverter
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
